// @ts-check
// Regras puras da referência da API na SPA (contrato 08 da API): agrupa os endpoints da especificação OpenAPI por
// objeto, descreve tipos de esquema e monta o exemplo de chamada com curl.

const HTTP_METHODS = ['get', 'post', 'put', 'patch', 'delete'];
// exemplos de valor por tipo, para o corpo do exemplo de chamada
const EXAMPLE_FIELD_LIMIT = 3;

/**
 * @typedef {{ method: string, path: string, operation: any }} Endpoint
 * @typedef {{ tag: string, endpoints: Endpoint[], schemaName: string | null }} ApiGroup
 */

/**
 * Agrupa os endpoints da especificação pelo tag (um por objeto) e acha o nome do esquema do objeto.
 * @param {any} spec
 * @returns {ApiGroup[]}
 */
export function groupEndpointsByTag(spec) {
  /** @type {Map<string, ApiGroup>} */
  const groups = new Map();
  for (const [path, pathItem] of Object.entries(spec?.paths ?? {})) {
    for (const method of HTTP_METHODS) {
      const operation = /** @type {any} */ (pathItem)?.[method];
      if (!operation) continue;
      const tag = operation.tags?.[0] ?? 'API';
      const group = groups.get(tag) ?? { tag, endpoints: [], schemaName: null };
      group.endpoints.push({ method, path, operation });
      const match = /^findOne(\w+)$/.exec(operation.operationId ?? '');
      if (match && spec?.components?.schemas?.[`${match[1]}ForResponse`]) group.schemaName = match[1];
      groups.set(tag, group);
    }
  }
  return [...groups.values()];
}

/**
 * Tipo legível de um esquema JSON: string, number, uuid, date-time, enum (A, B), objeto, lista de...
 * @param {any} schema
 * @returns {string}
 */
export function describeSchemaType(schema) {
  if (!schema) return '-';
  if (schema.$ref) return String(schema.$ref).split('/').pop() ?? '-';
  const types = (Array.isArray(schema.type) ? schema.type : [schema.type]).filter((type) => type && type !== 'null');
  const type = types[0];
  if (type === 'array') return `${describeSchemaType(schema.items)}[]`;
  if (Array.isArray(schema.enum)) return `enum (${schema.enum.slice(0, 6).join(', ')}${schema.enum.length > 6 ? ', ...' : ''})`;
  if (schema.format) return schema.format;
  if (type === 'object' && schema.properties) return `{ ${Object.keys(schema.properties).join(', ')} }`;
  return type ?? 'json';
}

/** @param {any} schema */
function exampleValue(schema) {
  if (Array.isArray(schema?.enum) && schema.enum.length > 0) return schema.enum[0];
  const type = (Array.isArray(schema?.type) ? schema.type : [schema?.type]).find((candidate) => candidate && candidate !== 'null');
  if (schema?.format === 'uuid') return '00000000-0000-0000-0000-000000000000';
  if (schema?.format === 'date') return '2026-03-01';
  if (schema?.format === 'date-time') return '2026-03-01T12:00:00.000Z';
  if (type === 'number') return 1;
  if (type === 'boolean') return true;
  if (type === 'array') return [];
  if (type === 'object' && schema.properties) {
    return Object.fromEntries(Object.entries(schema.properties).slice(0, 2).map(([name, property]) => [name, exampleValue(property)]));
  }
  return 'texto';
}

/**
 * Exemplo de chamada com curl para um endpoint (Bearer da chave e corpo com alguns campos do objeto).
 * @param {{ baseUrl: string, endpoint: Endpoint, spec: any }} input
 */
export function buildCurlExample({ baseUrl, endpoint, spec }) {
  const path = endpoint.path.replace('{id}', '<id>');
  // PATCH e DELETE na coleção pedem o filtro
  const isCollectionWrite = (endpoint.method === 'patch' || endpoint.method === 'delete') && !endpoint.path.includes('{id}') && !endpoint.path.endsWith('/merge');
  const query = isCollectionWrite ? '?filter=id[in]:[<id1>,<id2>]' : '';
  // -g: o curl não interpreta os colchetes do filtro
  const lines = [`curl${query ? ' -g' : ''} -X ${endpoint.method.toUpperCase()} '${baseUrl}${path}${query}'`, '  -H "Authorization: Bearer mm_..."'];
  const bodySchema = endpoint.operation.requestBody?.content?.['application/json']?.schema;
  if (bodySchema) {
    const resolved = bodySchema.$ref ? spec?.components?.schemas?.[String(bodySchema.$ref).split('/').pop() ?? ''] : bodySchema;
    const target = resolved?.type === 'array' && resolved.items?.$ref ? spec?.components?.schemas?.[String(resolved.items.$ref).split('/').pop() ?? ''] : resolved;
    const required = new Set(target?.required ?? []);
    const entries = Object.entries(target?.properties ?? {})
      .sort(([first], [second]) => Number(required.has(second)) - Number(required.has(first)))
      .slice(0, EXAMPLE_FIELD_LIMIT)
      .map(([name, property]) => [name, exampleValue(property)]);
    const example = Object.fromEntries(entries);
    lines.push('  -H "Content-Type: application/json"', `  -d '${JSON.stringify(resolved?.type === 'array' ? [example] : example)}'`);
  }
  return lines.join(' \\\n');
}
